import { html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { BaseElement, isAuthenticated } from "../app.js";
import heroUrl from "../assets/hero.webp";

import "../components/header-component.ts";
import "../components/footer-component.ts";
import "../components/pricing-component.ts";

// Preload hero image only on root route (before component renders)
if (location.pathname === "/") {
    document.head.appendChild(Object.assign(document.createElement("link"), { rel: "preload", as: "image", href: heroUrl }));
}

@customElement("main-page")
export class MainPage extends BaseElement {
    @state() mail = "";
    @state() isMailValid = false;
    @state() loading = false;

    firstUpdated() {
        // Redirect authenticated users to dashboard only if they're not coming from within the app
        if (isAuthenticated()) {
            const referrer = document.referrer;
            const currentHost = window.location.host;
            const isFromSameApp = referrer && new URL(referrer).host === currentHost;

            if (!isFromSameApp) {
                window.location.href = "/search";
                return;
            }
        }

        const pricingComponent = this.querySelector("pricing-component");
        if (pricingComponent) {
            pricingComponent.addEventListener("pricing-button-click", () => this.handlePricingButtonClick());
        }
    }

    handlePricingButtonClick() {
        if (isAuthenticated()) {
            window.location.href = "/pricing";
        } else {
            window.location.href = "/login";
        }
    }

    validateEmail(email: string): boolean {
        // Simple regex for email validation
        const emailRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,}$/;
        return emailRegex.test(email);
    }

    handleInput(e: InputEvent) {
        this.mail = (e.target as HTMLInputElement).value;
        this.isMailValid = this.validateEmail(this.mail);
    }

    render() {
        return html`
            <div class="flex flex-col h-screen container max-w-5xl p-4 md:p-6 lg:p8">
                <header-component></header-component>

                <main>
                    <p class="text-2xl md:text-3xl text-center mb-8 mt-4">Find and track keywords on the Apple AppStore to grow your downloads.</p>

                    <img src=${heroUrl} alt="AppShovel Keyword Lists" class="mx-auto" width="2048" height="1592" fetchpriority="high" />

                    <br />

                    <h2>Pricing</h2>

                    <pricing-component buttonTitle="Start 14-Day Free Trial"></pricing-component>

                    <br />

                    <h2>FAQ</h2>
                    <h3>What is App Store Optimization?</h3>
                    <p>
                        App Store Optimization (ASO) is the process of improving an app's visibility in app stores like the Apple App Store or Google
                        Play Store. It involves optimizing elements like the app title, description, keywords, visuals, and user ratings to drive more
                        organic downloads.
                    </p>
                    <p>
                        Keywords are essential in App Store Optimization because they determine how easily users can find your app in search results.
                        By strategically selecting and optimizing keywords, you increase your app's visibility for relevant searches, leading to more
                        organic downloads and better ranking within the app store.
                    </p>
                    <br />
                    <h3>How does AppShovel get keyword popularity?</h3>
                    <p>
                        AppShovel fetches keyword popularity data directly from Apple Search Ads, which is Apple's advertising platform for promoting
                        apps in the App Store. Apple Search Ads provides a keyword popularity score that reflects how often a specific keyword is
                        searched by users in the App Store.
                    </p>
                    <br />
                </main>

                <footer-component></footer-component>
            </div>
        `;
    }
}
